import type { Router } from 'vue-router'

const DEFAULT_REDIRECT = '/'
const LOGIN_PATH = '/login'
const AUTH_HASH_KEY = 'airin-auth'

export interface RedirectSessionPayload {
  token: string
  user?: unknown
  tenant?: unknown
}

function decodeRedirect(value: string): string {
  let decoded = value.trim()

  for (let index = 0; index < 2; index += 1) {
    try {
      const nextValue = decodeURIComponent(decoded)
      if (nextValue === decoded) {
        break
      }
      decoded = nextValue
    } catch {
      break
    }
  }

  return decoded
}

function isTrustedExternalOrigin(target: URL): boolean {
  const hostname = target.hostname.toLowerCase()
  const currentHostname = window.location.hostname.toLowerCase()

  return (
    hostname === currentHostname ||
    hostname === 'localhost' ||
    hostname.endsWith('.tuev-risk-navigator.ai') ||
    hostname.endsWith('.risk-navigator.ai')
  )
}

export function appendAuthPayload(target: URL, authPayload?: RedirectSessionPayload): string {
  if (!authPayload?.token) {
    return target.toString()
  }

  const payload = {
    token: authPayload.token,
    user: authPayload.user,
    tenant: authPayload.tenant,
    redirectHash: target.hash || undefined
  }

  target.hash = `${AUTH_HASH_KEY}=${encodeURIComponent(JSON.stringify(payload))}`
  return target.toString()
}

function resolveRedirectTarget(
  redirect: unknown,
  authPayload?: RedirectSessionPayload
): { external: boolean; target: string } | null {
  if (typeof window === 'undefined' || typeof redirect !== 'string') {
    return null
  }

  const decodedRedirect = decodeRedirect(redirect)
  if (!decodedRedirect) {
    return null
  }

  let resolvedUrl: URL

  try {
    resolvedUrl = new URL(decodedRedirect, window.location.origin)
  } catch {
    return null
  }

  if (resolvedUrl.pathname === LOGIN_PATH) {
    return null
  }

  if (resolvedUrl.origin !== window.location.origin) {
    if (!isTrustedExternalOrigin(resolvedUrl)) {
      return null
    }

    return {
      external: true,
      target: appendAuthPayload(resolvedUrl, authPayload)
    }
  }

  return {
    external: false,
    target: `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`
  }
}

async function navigateToRedirect(
  router: Router,
  method: 'push' | 'replace',
  redirect: unknown,
  authPayload?: RedirectSessionPayload
) {
  const resolved = resolveRedirectTarget(redirect, authPayload)

  if (!resolved) {
    if (method === 'replace') {
      await router.replace(DEFAULT_REDIRECT)
      return
    }

    await router.push(DEFAULT_REDIRECT)
    return
  }

  if (resolved.external) {
    window.location.assign(resolved.target)
    return
  }

  if (method === 'replace') {
    await router.replace(resolved.target)
    return
  }

  await router.push(resolved.target)
}

function safeParse<T>(raw: string | null): T | undefined {
  if (!raw) {
    return undefined
  }

  try {
    return JSON.parse(raw) as T
  } catch {
    return undefined
  }
}

export function getStoredRedirectSessionPayload(): RedirectSessionPayload | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }

  const token = localStorage.getItem('token')
  if (!token) {
    return undefined
  }

  return {
    token,
    user: safeParse(localStorage.getItem('user')),
    tenant: safeParse(localStorage.getItem('tenant'))
  }
}

export async function pushRedirect(
  router: Router,
  redirect: unknown,
  authPayload?: RedirectSessionPayload
) {
  await navigateToRedirect(router, 'push', redirect, authPayload)
}

export async function replaceRedirect(
  router: Router,
  redirect: unknown,
  authPayload?: RedirectSessionPayload
) {
  await navigateToRedirect(router, 'replace', redirect, authPayload)
}
